import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import {
	TEMPLATE_MODIFY_DENIED_MESSAGE,
	canModifyTemplate,
} from '@/features/templates/templatePermissions';
import { getUnlayerTags } from '@/utils/unlayer';
import { showToast } from '@/features/toast/toastSlice';
import { openDialog } from '@/features/dialog/dialogSlice';
import { uploadEmailAttachments } from '@/context/UnlayerDraftContext';
import {
	CREATE_NEW_SENTINEL,
	UNLAYER_EDITOR_MIN_HEIGHT,
	buildUnlayerUserContext,
	getDesignFingerprint,
	parseDesignJsonInput,
	sortTemplates,
} from './helpers';

const EMPTY_TEMPLATE_VALUES = {
	name: '',
	subject: '',
	type: '',
	active: false,
	master: false,
	brokerGroupIds: [],
	workflowType: '',
	files: [],
};

const EMPTY_TEMPLATE_ERRORS = {
	name: '',
	subject: '',
	type: '',
};

const createEmptyDialogs = () => ({
	delete: { open: false },
	discard: { open: false },
	saveAs: { open: false, name: '', error: '' },
	loadDesignJson: { open: false, value: '', error: '' },
});

const EMPTY_DRAWER_STATE = {
	isOpen: false,
	isCreateMode: false,
	selectedTemplateId: '',
	selectedTemplateSubject: '',
	pendingTemplateId: '',
	isPendingClose: false,
};

const useNoop = () => {
	const noop = useCallback(async () => {}, []);
	return [noop, { isLoading: false }];
};

const useUnlayerTemplatesEditorImpl = ({
	labels = {},
	useLazyGetSummariesQuery,
	useLazyGetDetailQuery,
	useCreateMutation,
	useUpdateMutation,
	useDeleteMutation,
	useShareMutation,
	setActiveTemplate = null,
	useUpdatePropertiesMutation = null,
	requireUnlayerEmailsFlag = true,
	defaultSortField = 'lastUpdated',
	defaultSortDirection = 'desc',
}) => {
	const dispatch = useDispatch();
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const { getFeatureFlags, isSuccess, isFetching } = useFeatureFlagContext();

	const showUnlayerEmails = Boolean(getFeatureFlags('unlayerEmails')?.active);

	const userBrokerGroupIds = userDetails?.brokerGroupArray ?? [];
	const isFolioAdmin = Boolean(userDetails?.isFolioAdmin);

	const [listState, setListState] = useState({
		sortField: defaultSortField,
		sortDirection: defaultSortDirection,
	});
	const [drawerState, setDrawerState] = useState(EMPTY_DRAWER_STATE);
	const [templateForm, setTemplateForm] = useState({
		values: EMPTY_TEMPLATE_VALUES,
		errors: EMPTY_TEMPLATE_ERRORS,
		designToLoad: null,
		editorDesign: null,
		editorInstanceKey: 0,
		initialSnapshot: null,
	});
	const [dialogs, setDialogs] = useState(createEmptyDialogs);
	const [editorMinHeight, setEditorMinHeight] = useState(UNLAYER_EDITOR_MIN_HEIGHT);
	const isTogglingActiveRef = useRef(false);

	const isAiTemplateEditor = Boolean(labels.showAiTemplateFields);
	const { values, errors, designToLoad, editorDesign, editorInstanceKey, initialSnapshot } =
		templateForm;
	const { sortField, sortDirection } = listState;

	const canModifyTemplateRow = useCallback(
		(template) => {
			if (!template) {
				return false;
			}
			if (isAiTemplateEditor && template.master) {
				return false;
			}
			return canModifyTemplate({
				user: userDetails,
				template,
			});
		},
		[isAiTemplateEditor, userDetails],
	);

	const patchDrawer = useCallback((patch) => {
		setDrawerState((prev) => ({ ...prev, ...patch }));
	}, []);

	const patchTemplateForm = useCallback((patch) => {
		setTemplateForm((prev) => ({ ...prev, ...patch }));
	}, []);

	const patchTemplateValues = useCallback((patch) => {
		setTemplateForm((prev) => ({
			...prev,
			values: { ...prev.values, ...patch },
		}));
	}, []);

	const patchTemplateErrors = useCallback((patch) => {
		setTemplateForm((prev) => ({
			...prev,
			errors: { ...prev.errors, ...patch },
		}));
	}, []);

	const patchDialog = useCallback((key, patch) => {
		setDialogs((prev) => ({
			...prev,
			[key]: { ...prev[key], ...patch },
		}));
	}, []);

	const [
		loadTemplateSummaries,
		{ data: templateSummaries = [], isFetching: isFetchingTemplates },
	] = useLazyGetSummariesQuery();
	const [loadTemplateDetail, { isFetching: isFetchingTemplateDetail }] = useLazyGetDetailQuery();
	const [createTemplate, { isLoading: isCreatingTemplate }] = useCreateMutation();
	const [updateTemplate, { isLoading: isUpdatingFullTemplate }] = useUpdateMutation();
	const [deleteTemplate, { isLoading: isDeletingTemplate }] = useDeleteMutation();
	const useShareTemplateHook = useShareMutation ?? useNoop;
	const [shareTemplate, { isLoading: isSharingTemplate }] = useShareTemplateHook();
	const useUpdatePropertiesHook = useUpdatePropertiesMutation ?? useNoop;
	const [updateTemplateProperties, { isLoading: isUpdatingProperties }] =
		useUpdatePropertiesHook();

	const isUpdatingTemplate = isUpdatingFullTemplate || isUpdatingProperties;
	const shareDialogData = useSelector((state) => state.dialog.data?.share);
	const supportsAttachments = !isAiTemplateEditor;

	const mergeTags = useMemo(() => {
		const unlayerUserContext = buildUnlayerUserContext({
			userDetails,
			fallbackEmail: email,
		});
		return getUnlayerTags(unlayerUserContext, null, null);
	}, [email, userDetails]);

	const reloadTemplateSummaries = useCallback(async () => {
		// The AI template list resolves its own owner/brokerGroup scope from the
		// logged-in user, so it always runs; the regular template list needs
		// brokerGroupIds passed in and is skipped when the feature flag is off or
		// the user has no broker groups.
		if (
			!isAiTemplateEditor &&
			((requireUnlayerEmailsFlag && !showUnlayerEmails) || userBrokerGroupIds.length === 0)
		) {
			return [];
		}

		try {
			return await loadTemplateSummaries(
				isAiTemplateEditor ? {} : { brokerGroupIds: userBrokerGroupIds },
			).unwrap();
		} catch (error) {
			console.error('Error reloading template summaries', error);
			return [];
		}
	}, [
		isAiTemplateEditor,
		loadTemplateSummaries,
		requireUnlayerEmailsFlag,
		showUnlayerEmails,
		userBrokerGroupIds,
		userDetails?.userId,
	]);

	useEffect(() => {
		reloadTemplateSummaries();
	}, [reloadTemplateSummaries]);

	useEffect(() => {
		const calculateEditorHeight = () =>
			setEditorMinHeight(Math.max(UNLAYER_EDITOR_MIN_HEIGHT, window.innerHeight - 220));
		calculateEditorHeight();
		window.addEventListener('resize', calculateEditorHeight);
		return () => window.removeEventListener('resize', calculateEditorHeight);
	}, []);

	const sortedTemplates = useMemo(
		() => sortTemplates(templateSummaries, sortField, sortDirection),
		[sortDirection, sortField, templateSummaries],
	);

	const currentFingerprint = getDesignFingerprint(editorDesign);
	const isDirty = Boolean(
		drawerState.isOpen &&
		initialSnapshot &&
		(values.name.trim() !== initialSnapshot.name ||
			values.subject.trim() !== initialSnapshot.subject ||
			(isAiTemplateEditor && values.active !== initialSnapshot.active) ||
			(isAiTemplateEditor &&
				(values.workflowType || '') !== (initialSnapshot.workflowType || '')) ||
			(isAiTemplateEditor &&
				JSON.stringify(values.brokerGroupIds) !==
					JSON.stringify(initialSnapshot.brokerGroupIds ?? [])) ||
			(supportsAttachments &&
				(values.files?.length ?? 0) !== (initialSnapshot.filesCount ?? 0)) ||
			currentFingerprint !== initialSnapshot.fingerprint),
	);

	const selectedTemplateSummary = useMemo(
		() =>
			templateSummaries.find((template) => template.id === drawerState.selectedTemplateId) ??
			null,
		[drawerState.selectedTemplateId, templateSummaries],
	);

	const canModifySelectedTemplate = useMemo(() => {
		if (drawerState.isCreateMode || !drawerState.selectedTemplateId) {
			return true;
		}
		return canModifyTemplateRow(selectedTemplateSummary);
	}, [
		canModifyTemplateRow,
		drawerState.isCreateMode,
		drawerState.selectedTemplateId,
		selectedTemplateSummary,
	]);

	const clearDrawerState = useCallback(() => {
		setDrawerState(EMPTY_DRAWER_STATE);
		setTemplateForm((prev) => ({
			values: EMPTY_TEMPLATE_VALUES,
			errors: EMPTY_TEMPLATE_ERRORS,
			designToLoad: null,
			editorDesign: null,
			editorInstanceKey: prev.editorInstanceKey + 1,
			initialSnapshot: null,
		}));
		setDialogs(createEmptyDialogs());
	}, []);

	const openTemplateInDrawer = useCallback(
		async (templateId) => {
			const detail = await loadTemplateDetail({ id: templateId }).unwrap();
			const nextName = detail?.name ?? '';
			const nextSubject = detail?.subject ?? '';
			const nextDesign = detail?.designJson ?? null;
			const nextType = detail?.type ?? '';
			const nextActive = Boolean(detail?.active);
			const nextWorkflowType = detail?.workflowType ?? '';
			const nextMaster = Boolean(detail?.master);
			const nextBrokerGroupIds = detail?.brokerGroupIds ?? [];
			const nextFiles = supportsAttachments
				? (detail?.attachments ?? []).map((attachment) => ({
						name: attachment.fileName,
						size: attachment.fileSize,
						url: attachment.downloadUrl,
						downloadUrl: attachment.downloadUrl,
						mimeType: attachment.mimeType ?? attachment.type,
						storagePath: attachment.storagePath,
					}))
				: [];

			setDrawerState({
				isOpen: true,
				isCreateMode: false,
				selectedTemplateId: templateId,
				selectedTemplateSubject: nextSubject,
				pendingTemplateId: '',
				isPendingClose: false,
			});
			setTemplateForm((prev) => ({
				values: {
					name: nextName,
					subject: nextSubject,
					type: nextType,
					active: nextActive,
					master: nextMaster,
					brokerGroupIds: nextBrokerGroupIds,
					workflowType: nextWorkflowType,
					files: nextFiles,
				},
				errors: EMPTY_TEMPLATE_ERRORS,
				designToLoad: nextDesign,
				editorDesign: nextDesign,
				editorInstanceKey: prev.editorInstanceKey + 1,
				initialSnapshot: {
					name: nextName.trim(),
					subject: nextSubject.trim(),
					type: nextType.trim(),
					active: nextActive,
					workflowType: nextWorkflowType,
					brokerGroupIds: nextBrokerGroupIds,
					fingerprint: getDesignFingerprint(nextDesign),
					filesCount: nextFiles.length,
				},
			}));
		},
		[loadTemplateDetail, supportsAttachments],
	);

	const openCreateDrawer = useCallback(() => {
		setDrawerState({
			isOpen: true,
			isCreateMode: true,
			selectedTemplateId: '',
			selectedTemplateSubject: '',
			pendingTemplateId: '',
			isPendingClose: false,
		});
		setTemplateForm((prev) => ({
			values: {
				...EMPTY_TEMPLATE_VALUES,
				brokerGroupIds: userBrokerGroupIds,
			},
			errors: EMPTY_TEMPLATE_ERRORS,
			designToLoad: null,
			editorDesign: null,
			editorInstanceKey: prev.editorInstanceKey + 1,
			initialSnapshot: {
				name: '',
				subject: '',
				type: '',
				active: false,
				workflowType: '',
				brokerGroupIds: userBrokerGroupIds,
				fingerprint: getDesignFingerprint(null),
				filesCount: 0,
			},
		}));
	}, [userBrokerGroupIds]);

	const requestOpenTemplate = useCallback(
		(templateId) => {
			if (isDirty) {
				patchDrawer({
					pendingTemplateId: templateId,
					isPendingClose: false,
				});
				patchDialog('discard', { open: true });
				return;
			}
			openTemplateInDrawer(templateId).catch(() => {
				dispatch(
					showToast({
						message: labels.loadTemplateError,
						error: true,
					}),
				);
			});
		},
		[
			dispatch,
			isDirty,
			labels.loadTemplateError,
			openTemplateInDrawer,
			patchDialog,
			patchDrawer,
		],
	);

	const requestCreateTemplate = useCallback(() => {
		if (isDirty) {
			patchDrawer({
				pendingTemplateId: CREATE_NEW_SENTINEL,
				isPendingClose: false,
			});
			patchDialog('discard', { open: true });
			return;
		}
		openCreateDrawer();
	}, [isDirty, openCreateDrawer, patchDialog, patchDrawer]);

	const requestCloseDrawer = useCallback(() => {
		if (!drawerState.isOpen) return;
		if (isDirty) {
			patchDrawer({
				isPendingClose: true,
				pendingTemplateId: '',
			});
			patchDialog('discard', { open: true });
			return;
		}
		clearDrawerState();
	}, [clearDrawerState, drawerState.isOpen, isDirty, patchDialog, patchDrawer]);

	const buildTemplatePayload = useCallback(
		({ id, name, subject, designJson, attachments = [], forceInactive = false }) => ({
			...(id ? { id } : {}),
			name,
			subject,
			designJson: designJson ?? {},
			brokerGroupIds: isAiTemplateEditor ? values.brokerGroupIds : userBrokerGroupIds,
			...(supportsAttachments ? { attachments } : {}),
			...(isAiTemplateEditor
				? {
						type: values.type.trim(),
						// forceInactive covers handleSaveAsTemplate, which always creates a
						// brand-new, unshared copy — it must start inactive rather than inheriting
						// the source template's active state (updates to the master itself are
						// blocked earlier, so values.master here is also only ever reached via
						// handleSaveAsTemplate).
						active: forceInactive || values.master ? false : values.active,
						workflowType: values.master
							? (initialSnapshot?.workflowType ?? '')
							: (values.workflowType ?? ''),
						master: values.master,
					}
				: {}),
		}),
		[
			isAiTemplateEditor,
			initialSnapshot?.workflowType,
			supportsAttachments,
			userBrokerGroupIds,
			values.active,
			values.brokerGroupIds,
			values.master,
			values.type,
			values.workflowType,
		],
	);

	const handleSaveTemplate = async () => {
		if (isAiTemplateEditor && values.master) {
			return;
		}

		const trimmedName = values.name.trim();
		if (!trimmedName) {
			patchTemplateErrors({ name: 'Template name is required.' });
			return;
		}

		if (!userDetails?.userId) {
			dispatch(
				showToast({
					message: labels.missingUserError,
					error: true,
				}),
			);
			return;
		}

		const isCreating = drawerState.isCreateMode || !drawerState.selectedTemplateId;

		if (!isCreating && !canModifySelectedTemplate) {
			dispatch(
				showToast({
					message: TEMPLATE_MODIFY_DENIED_MESSAGE,
					error: true,
				}),
			);
			return;
		}

		if (
			isAiTemplateEditor &&
			values.active &&
			!(values.brokerGroupIds ?? []).map(String).filter(Boolean).length
		) {
			dispatch(
				showToast({
					message:
						'Share the template with at least one broker group before setting it active',
					error: true,
				}),
			);
			return;
		}

		let savedTemplateId = drawerState.selectedTemplateId;
		let savedAttachments = [];

		try {
			if (isCreating) {
				const createdTemplate = await createTemplate(
					buildTemplatePayload({
						name: trimmedName,
						subject: values.subject.trim(),
						designJson: editorDesign,
					}),
				).unwrap();
				savedTemplateId = createdTemplate?.id ?? '';

				if (supportsAttachments && savedTemplateId) {
					savedAttachments = await uploadEmailAttachments(
						values.files ?? [],
						userDetails?.userId,
						`unlayerEmailTemplates/${savedTemplateId}/attachments`,
					);
					if (savedAttachments.length > 0) {
						await updateTemplate({
							id: savedTemplateId,
							...buildTemplatePayload({
								name: trimmedName,
								subject: values.subject.trim(),
								designJson: editorDesign,
								attachments: savedAttachments,
							}),
						}).unwrap();
					}
				}

				await reloadTemplateSummaries();
				dispatch(
					showToast({
						message: labels.saveCreateSuccess,
						autohide: true,
					}),
				);

				if (isAiTemplateEditor) {
					clearDrawerState();
					return;
				}

				patchDrawer({
					isCreateMode: false,
					selectedTemplateId: savedTemplateId,
				});
			} else {
				if (supportsAttachments) {
					savedAttachments = await uploadEmailAttachments(
						values.files ?? [],
						userDetails?.userId,
						`unlayerEmailTemplates/${drawerState.selectedTemplateId}/attachments`,
					);
				}
				await updateTemplate({
					id: drawerState.selectedTemplateId,
					...buildTemplatePayload({
						name: trimmedName,
						subject: values.subject.trim(),
						designJson: editorDesign,
						attachments: savedAttachments,
					}),
				}).unwrap();
			}

			const summaries = await reloadTemplateSummaries();
			const savedTemplate = summaries.find((row) => row.id === savedTemplateId);
			const actualActive = Boolean(savedTemplate?.active ?? values.active);
			const nextFiles = supportsAttachments
				? (savedAttachments ?? []).map((attachment) => ({
						name: attachment.fileName,
						size: attachment.fileSize,
						url: attachment.downloadUrl,
						downloadUrl: attachment.downloadUrl,
						mimeType: attachment.mimeType,
						storagePath: attachment.storagePath,
					}))
				: [];

			setTemplateForm((prev) => ({
				...prev,
				values: {
					...prev.values,
					active: actualActive,
					files: nextFiles,
				},
				initialSnapshot: {
					name: trimmedName,
					subject: prev.values.subject.trim(),
					type: prev.values.type.trim(),
					active: actualActive,
					workflowType: prev.values.workflowType ?? '',
					brokerGroupIds: prev.values.brokerGroupIds,
					fingerprint: getDesignFingerprint(prev.editorDesign),
					filesCount: nextFiles.length,
				},
			}));

			if (!isCreating) {
				dispatch(
					showToast({
						message: labels.saveUpdateSuccess,
						autohide: true,
					}),
				);
			}
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.message ?? labels.saveError,
					error: true,
				}),
			);
		}
	};

	const handleUpdateTemplateProperties = useCallback(
		async ({ id, properties }) => {
			if (!id || !properties || Object.keys(properties).length === 0) {
				return;
			}

			if (!useUpdatePropertiesMutation) {
				return;
			}

			const template = templateSummaries.find((row) => row.id === id);
			if (!canModifyTemplateRow(template)) {
				dispatch(
					showToast({
						message: TEMPLATE_MODIFY_DENIED_MESSAGE,
						error: true,
					}),
				);
				return;
			}

			await updateTemplateProperties({ id, properties }).unwrap();
			await reloadTemplateSummaries();

			if (drawerState.selectedTemplateId === id) {
				setTemplateForm((prev) => ({
					...prev,
					values: {
						...prev.values,
						...properties,
					},
					initialSnapshot: prev.initialSnapshot
						? {
								...prev.initialSnapshot,
								...properties,
							}
						: prev.initialSnapshot,
				}));
			}
		},
		[
			canModifyTemplateRow,
			dispatch,
			drawerState.selectedTemplateId,
			reloadTemplateSummaries,
			templateSummaries,
			updateTemplateProperties,
			useUpdatePropertiesMutation,
		],
	);

	const handleToggleTemplateActive = useCallback(
		async (templateId, nextActive) => {
			if (!templateId || !setActiveTemplate || isTogglingActiveRef.current) {
				return;
			}

			const template = templateSummaries.find((row) => row.id === templateId) ?? null;

			if (!template || template.master) {
				return;
			}

			if (!canModifyTemplateRow(template)) {
				dispatch(
					showToast({
						message: TEMPLATE_MODIFY_DENIED_MESSAGE,
						error: true,
					}),
				);
				return;
			}

			if (nextActive === Boolean(template.active)) {
				return;
			}

			if (nextActive && !(template.brokerGroupIds ?? []).length) {
				dispatch(
					showToast({
						message:
							'Share the template with at least one broker group before setting it active',
						error: true,
					}),
				);
				return;
			}

			isTogglingActiveRef.current = true;

			try {
				await setActiveTemplate({
					id: templateId,
					active: nextActive,
					brokerGroupIds: template.brokerGroupIds ?? [],
					workflowType: template.workflowType ?? '',
				}).unwrap();

				if (drawerState.selectedTemplateId === templateId) {
					setTemplateForm((prev) => ({
						...prev,
						values: {
							...prev.values,
							active: nextActive,
						},
						initialSnapshot: prev.initialSnapshot
							? { ...prev.initialSnapshot, active: nextActive }
							: prev.initialSnapshot,
					}));
				}
			} catch (error) {
				console.error('Error toggling AI template active status', error);
			} finally {
				isTogglingActiveRef.current = false;
			}
		},
		[
			canModifyTemplateRow,
			dispatch,
			drawerState.selectedTemplateId,
			setActiveTemplate,
			templateSummaries,
		],
	);

	const handleOpenShareDialog = useCallback(
		(template) => {
			if (!template?.id || template?.master || !useShareMutation) {
				return;
			}

			if (!canModifyTemplateRow(template)) {
				dispatch(
					showToast({
						message: TEMPLATE_MODIFY_DENIED_MESSAGE,
						error: true,
					}),
				);
				return;
			}

			dispatch(
				openDialog({
					dialogName: 'share',
					data: {
						id: template.id,
						name: template.name,
						brokerGroupIds: template.brokerGroupIds ?? [],
					},
				}),
			);
		},
		[canModifyTemplateRow, dispatch, useShareMutation],
	);

	const handleShareTemplate = useCallback(
		async (_userIds, brokerGroupIds) => {
			const templateId = shareDialogData?.id;
			if (!templateId || !useShareMutation) {
				return;
			}

			try {
				await shareTemplate({ id: templateId, brokerGroupIds }).unwrap();
				dispatch(
					showToast({
						message: labels.shareSuccess,
						autohide: true,
					}),
				);
			} catch (error) {
				dispatch(
					showToast({
						message: error?.data?.message ?? labels.shareError,
						error: true,
					}),
				);
			}
		},
		[
			dispatch,
			labels.shareError,
			labels.shareSuccess,
			shareDialogData?.id,
			shareTemplate,
			useShareMutation,
		],
	);

	const handleDeleteTemplate = async () => {
		if (!drawerState.selectedTemplateId || (isAiTemplateEditor && values.master)) {
			return;
		}

		if (!canModifySelectedTemplate) {
			dispatch(
				showToast({
					message: TEMPLATE_MODIFY_DENIED_MESSAGE,
					error: true,
				}),
			);
			return;
		}

		try {
			await deleteTemplate({ id: drawerState.selectedTemplateId }).unwrap();
			patchDialog('delete', { open: false });
			clearDrawerState();
			await reloadTemplateSummaries();
			dispatch(
				showToast({
					message: labels.deleteSuccess,
					autohide: true,
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.message ?? labels.deleteError,
					error: true,
				}),
			);
		}
	};

	const handleOpenSaveAsDialog = () => {
		patchDialog('saveAs', { open: true, name: '', error: '' });
	};

	const handleSaveAsTemplate = async () => {
		const trimmedName = dialogs.saveAs.name.trim();
		if (!trimmedName) {
			patchDialog('saveAs', { error: 'Template name is required.' });
			return;
		}

		if (!userDetails?.userId) {
			dispatch(
				showToast({
					message: labels.saveAsMissingUserError,
					error: true,
				}),
			);
			return;
		}

		try {
			const createdTemplate = await createTemplate(
				buildTemplatePayload({
					name: trimmedName,
					subject: values.subject.trim(),
					designJson: editorDesign,
					forceInactive: true,
				}),
			).unwrap();

			const savedTemplateId = createdTemplate?.id ?? '';
			let savedAttachments = [];
			let nextFiles = [];

			if (supportsAttachments && savedTemplateId) {
				savedAttachments = await uploadEmailAttachments(
					values.files ?? [],
					userDetails?.userId,
					`unlayerEmailTemplates/${savedTemplateId}/attachments`,
				);
				if (savedAttachments.length > 0) {
					await updateTemplate({
						id: savedTemplateId,
						...buildTemplatePayload({
							name: trimmedName,
							subject: values.subject.trim(),
							designJson: editorDesign,
							attachments: savedAttachments,
							forceInactive: true,
						}),
					}).unwrap();
				}
				nextFiles = (savedAttachments ?? []).map((attachment) => ({
					name: attachment.fileName,
					size: attachment.fileSize,
					url: attachment.downloadUrl,
					downloadUrl: attachment.downloadUrl,
					mimeType: attachment.mimeType,
					storagePath: attachment.storagePath,
				}));
			}

			await reloadTemplateSummaries();
			patchDrawer({
				isCreateMode: false,
				selectedTemplateId: savedTemplateId,
			});
			setTemplateForm((prev) => ({
				...prev,
				values: {
					...prev.values,
					name: trimmedName,
					...(isAiTemplateEditor ? { active: false, master: false } : {}),
					...(supportsAttachments ? { files: nextFiles } : {}),
				},
				initialSnapshot: {
					name: trimmedName,
					subject: prev.values.subject.trim(),
					type: prev.values.type.trim(),
					active: isAiTemplateEditor ? false : prev.values.active,
					workflowType: prev.values.workflowType ?? '',
					brokerGroupIds: prev.values.brokerGroupIds,
					fingerprint: getDesignFingerprint(prev.editorDesign),
					...(supportsAttachments ? { filesCount: nextFiles.length } : {}),
				},
			}));
			patchDialog('saveAs', { open: false, name: '', error: '' });
			dispatch(
				showToast({
					message: labels.saveAsSuccess,
					autohide: true,
				}),
			);
		} catch (error) {
			dispatch(
				showToast({
					message: error?.data?.message ?? labels.saveAsError,
					error: true,
				}),
			);
		}
	};

	const handleOpenLoadDesignDialog = () => {
		patchDialog('loadDesignJson', { open: true, error: '' });
	};

	const handleCloseLoadDesignDialog = () => {
		patchDialog('loadDesignJson', { open: false });
	};

	const handleLoadDesignJsonValueChange = (value) => {
		patchDialog('loadDesignJson', { value, error: '' });
	};

	const handleLoadDesignJson = () => {
		const result = parseDesignJsonInput(dialogs.loadDesignJson.value);
		if (result.error) {
			patchDialog('loadDesignJson', { error: result.error });
			return;
		}

		setTemplateForm((prev) => ({
			...prev,
			designToLoad: result.design,
			editorDesign: result.design,
			editorInstanceKey: prev.editorInstanceKey + 1,
		}));
		patchDialog('loadDesignJson', { open: false, value: '', error: '' });
	};

	const handleDiscardConfirm = () => {
		patchDialog('discard', { open: false });
		const nextTemplateId = drawerState.pendingTemplateId;
		const shouldClose = drawerState.isPendingClose;
		patchDrawer({
			pendingTemplateId: '',
			isPendingClose: false,
		});

		if (shouldClose) {
			clearDrawerState();
			return;
		}

		if (nextTemplateId === CREATE_NEW_SENTINEL) {
			openCreateDrawer();
			return;
		}

		if (nextTemplateId) {
			openTemplateInDrawer(nextTemplateId).catch(() => {
				dispatch(
					showToast({
						message: labels.loadTemplateError,
						error: true,
					}),
				);
			});
		}
	};

	const handleSort = (field) => {
		setListState((prev) => {
			if (prev.sortField === field) {
				return {
					...prev,
					sortDirection: prev.sortDirection === 'asc' ? 'desc' : 'asc',
				};
			}

			return {
				...prev,
				sortField: field,
				sortDirection: field === 'name' ? 'asc' : 'desc',
			};
		});
	};

	const handleInsertNameTag = useCallback(() => {
		setTemplateForm((prev) => ({
			...prev,
			values: {
				...prev.values,
				subject: `${prev.values.subject}{{client.name}}`,
			},
		}));
	}, []);

	const setEditorDesign = useCallback((design) => {
		setTemplateForm((prev) => ({
			...prev,
			editorDesign: design,
		}));
	}, []);

	return {
		labels,
		isSuccess,
		isFetching,
		showUnlayerEmails,
		listState,
		drawerState,
		templateForm,
		dialogs,
		editorMinHeight,
		patchDrawer,
		patchTemplateValues,
		patchTemplateErrors,
		patchTemplateForm,
		patchDialog,
		isFetchingTemplates,
		isFetchingTemplateDetail,
		isCreatingTemplate,
		isUpdatingTemplate,
		isDeletingTemplate,
		isSharingTemplate,
		sortedTemplates,
		showShareDialog: Boolean(useShareMutation),
		isFolioAdmin,
		canModifySelectedTemplate,
		canModifyTemplateRow,
		mergeTags,
		requestCreateTemplate,
		requestOpenTemplate,
		requestCloseDrawer,
		handleSaveTemplate,
		handleUpdateTemplateProperties,
		handleToggleTemplateActive,
		handleOpenShareDialog,
		handleShareTemplate,
		handleDeleteTemplate,
		handleOpenSaveAsDialog,
		handleSaveAsTemplate,
		handleOpenLoadDesignDialog,
		handleCloseLoadDesignDialog,
		handleLoadDesignJson,
		handleLoadDesignJsonValueChange,
		handleDiscardConfirm,
		handleSort,
		handleInsertNameTag,
		setEditorDesign,
	};
};

const useUnlayerTemplatesEditorWithActive = ({ useSetActiveMutation, ...options }) => {
	const [setActiveTemplate] = useSetActiveMutation();
	return useUnlayerTemplatesEditorImpl({ ...options, setActiveTemplate });
};

/**
 * Shared state and handlers for the Unlayer template editor. Accepts RTK Query hooks
 * so the same logic can power both standard email templates and AI email templates.
 */
export const useUnlayerTemplatesEditor = (options) => {
	const { useSetActiveMutation, ...rest } = options;

	if (useSetActiveMutation) {
		return useUnlayerTemplatesEditorWithActive({ ...rest, useSetActiveMutation });
	}

	return useUnlayerTemplatesEditorImpl(rest);
};
